import { LocalizationWithComponent } from '@/components/blocks/LocalizationWithComponent';
import { NumberInput } from '@/components/blocks/NumberInput';
import { OneClick } from '@/components/blocks/OneClick';
import { productDetailsQuantitySX } from '@/components/blocks/ProductDetails/styles/quantity';
import { OrderItemTableRowData } from '@/components/content/OrderItemTable/parts/Table';
import { orderItemTableIconSX } from '@/components/content/OrderItemTable/styles/orderItemTableIcon';
import { orderItemTableQuantitySX } from '@/components/content/OrderItemTable/styles/orderItemTableQuantity';
import DynamicIcon from '@/components/icons/DynamicIcon';
import { useCartSWRKey } from '@/data/Content/Cart';
import { useOrderItemTableRow } from '@/data/Content/OrderItemTable';
import { useLocalization } from '@/data/Localization';
import { ContentContext } from '@/data/context/content';
import { Order } from '@/data/types/Order';
import { ProductType } from '@/data/types/Product';
import { Stack, Typography } from '@mui/material';
import { debounce } from 'lodash';
import { useTrackAddToCart } from 'presentation/events/hooks/useTrackAddToCart';
import { useTrackException } from 'presentation/events/hooks/useTrackException';
import { useTrackRemoveFromCart } from 'presentation/events/hooks/useTrackRemoveFromCart';
import { FC, MouseEvent, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { MATERIAL_SYMBOL_ICONS } from 'shared/custom/constants/commonConstants';
import { unstable_serialize as serialize, useSWRConfig } from 'swr';
import { useEstimatedDeliveryDates } from '@/data/Content/EstimatedDeliveryDates';

export const OrderItemQuantity: FC<{ readOnly?: boolean; mini?: boolean }> = ({
	readOnly,
	mini = false,
}) => {
	const localization = useLocalization('productDetail');
	const {
		orderId,
		quantity: { quantity, onChange, unitQuantity },
		details,
		product,
		productsLoading,
	} = useContext(ContentContext) as OrderItemTableRowData &
		ReturnType<typeof useOrderItemTableRow> & {
			product?: ProductType;
			productsLoading?: boolean;
		};
	const cartSWRKey = useCartSWRKey();
	const { cache } = useSWRConfig();

	const minQuantityNumber = Number(unitQuantity);
	const partNumber = details.partNumber;

	const trackRemoveFromCart = useTrackRemoveFromCart();
	const trackAddToCart = useTrackAddToCart();
	const trackException = useTrackException();
	const estimatedDeliveryDates = useEstimatedDeliveryDates(product);
	const prevQuantityRef = useRef<number>(quantity);
	const prevPartNumberRef = useRef<string>(partNumber);
	const cartSnapshotRef = useRef<Order['orderItem']>([]);

	const isQuantityUpdateDisabled = readOnly || productsLoading || !product;

	useEffect(() => {
		const prev = prevQuantityRef.current;
		prevQuantityRef.current = quantity;

		if (prevPartNumberRef.current !== partNumber) {
			prevPartNumberRef.current = partNumber;
			return;
		}

		if (prev === quantity || !product) return;

		try {
			if (quantity < prev) {
				trackRemoveFromCart({
					product,
					quantity: prev - quantity,
					cachedCartItems: cartSnapshotRef.current,
				});
			} else {
				trackAddToCart({
					product,
					quantity: quantity - prev,
					cachedCartItems: cartSnapshotRef.current,
					...estimatedDeliveryDates,
					isEstimatedDeliveryDateDisplayed: true,
				});
			}
		} catch (e) {
			trackException(e);
		}
	}, [quantity, partNumber, product, trackAddToCart, trackRemoveFromCart, trackException]);

	const removeFromCart = async (event: MouseEvent<HTMLElement>) => {
		try {
			event.preventDefault();
			event.stopPropagation();

			if (!product) return;

			const cachedCartData = cache.get(serialize(cartSWRKey))?.data as Order;
			trackRemoveFromCart({
				product,
				quantity,
				cachedCartItems: cachedCartData?.orderItem ?? [],
			});

			await onChange(0);
		} catch (e) {
			trackException(e);
		}
	};

	const quantityChange = useCallback(
		async (updatedQuantity: number | null) => {
			try {
				if (updatedQuantity === null || updatedQuantity === quantity) return;

				const finalQuantity =
					updatedQuantity <= 0
						? 0
						: updatedQuantity < minQuantityNumber
						? minQuantityNumber
						: updatedQuantity;

				const cachedCartData = cache.get(serialize(cartSWRKey))?.data as Order;
				cartSnapshotRef.current = cachedCartData?.orderItem ?? [];

				await onChange(finalQuantity);
			} catch (e) {
				trackException(e);
			}
		},
		[quantity, minQuantityNumber, cache, cartSWRKey, onChange, trackException]
	);

	const quantityChangeRef = useRef(quantityChange);
	useEffect(() => {
		quantityChangeRef.current = quantityChange;
	}, [quantityChange]);

	const debouncedQuantityChange = useMemo(
		() =>
			debounce(
				(updatedQuantity: number | null) => quantityChangeRef.current(updatedQuantity),
				1500
			),
		[]
	);

	useEffect(() => () => debouncedQuantityChange.cancel(), [debouncedQuantityChange]);

	return (
		<Stack alignItems="center" direction="row" sx={orderItemTableQuantitySX}>
			{readOnly ? (
				mini ? (
					<LocalizationWithComponent
						text={localization.QuantityValue.t({ quantity })}
						components={[<Typography key="0" variant="body2" />, <Typography key="1" />]}
					/>
				) : (
					<Typography>{quantity}</Typography>
				)
			) : (
				<>
					<OneClick
						wrapper="icon"
						onClick={removeFromCart}
						spin
						spinSize={24}
						sx={{ color: 'secondary.main' }}
						disabled={isQuantityUpdateDisabled}
						className={mini ? 'quick-cart__delete-product' : undefined}
					>
						<DynamicIcon name={MATERIAL_SYMBOL_ICONS.delete} sx={orderItemTableIconSX} />
					</OneClick>
					<NumberInput
						onChange={debouncedQuantityChange}
						value={quantity}
						min={minQuantityNumber}
						sx={productDetailsQuantitySX.quantity(readOnly)}
						disabled={isQuantityUpdateDisabled}
						showControls
						disallowEmptyOnBlur
						data-testid="order-item-quantity"
						id="order-item-quantity"
						isControlled
						mini
					/>
				</>
			)}
		</Stack>
	);
};
